import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { JobCardSheetPreview } from './JobCardSheetPreview';
import { useJobCardPreview } from '@/hooks/useJobCardPreview';
import { downloadJobCardXlsx } from '@/lib/jobCardDownload';

interface JobCardPreviewModalProps {
  jobId: string;
  jobCode: string;
  /** The breakdown doc being viewed, when opened from the breakdown tab. */
  documentId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Shows what the job card will contain before downloading it. A preview failure
 * must never block getting the actual file, so Download stays available even
 * when the preview itself errored.
 */
export function JobCardPreviewModal({
  jobId,
  jobCode,
  documentId,
  open,
  onOpenChange,
}: JobCardPreviewModalProps) {
  const { data, isLoading, error } = useJobCardPreview(jobId, documentId, open);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      // Deliberately no alert() on the returned warning: whatever it says is
      // already on screen in the warnings banner above, and interrupting a
      // successful download with a popup is pure friction.
      await downloadJobCardXlsx({ jobId, jobCode, documentId });
      onOpenChange(false);
    } catch (err) {
      console.error('Error generating job card:', err);
      alert('Failed to generate job card. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Job Card Preview — {jobCode}</DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">Building job card preview...</div>
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertTitle>Could not build the preview</AlertTitle>
            <AlertDescription>
              {error.message} You can still download the job card below.
            </AlertDescription>
          </Alert>
        )}

        {/*
          The endpoint still returns `warnings` (the download path puts them on a
          header), but they are deliberately not rendered here: this is a clean
          preview of the card, not a validation surface. The one warning that is
          genuinely about the card's contents — workers that did not fit on the
          sheet — is shown inline in the worker section where it applies.
        */}
        {data && <JobCardSheetPreview payload={data.payload} />}

        <div className="flex justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={handleDownload} disabled={isDownloading}>
            {isDownloading ? 'Generating...' : 'Download .xlsx'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
